<template>
	<a
		class="collapse-sidebar-link ai-sidebar-item ai-header-button-root"
		:class="{ 'active-sidebar': hasActiveThread }"
		@click="openBrowser"
		:title="activeTitle"
		style="position: relative"
	>
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 16 16"
			width="14"
			height="14"
			fill="currentColor"
			class="ai-sidebar-icon"
		>
			<path
				d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.828zM3.794 1.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387A1.73 1.73 0 0 0 4.593 5.69l-.387 1.162a.217.217 0 0 1-.412 0L3.407 5.69A1.73 1.73 0 0 0 2.31 4.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387A1.73 1.73 0 0 0 3.407 2.31zM10.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732L9.1 2.137a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z"
			></path>
		</svg>
		<span class="sidebar-item-label ai-sidebar-label">{{ activeTitle }}</span>
		<div class="ai-status-indicator">
			<div
				v-show="isThinking"
				class="spinner-border spinner-border-sm ai-header-spinner"
				role="status"
				style="width: 14px; height: 14px; border-width: 0.15em; color: white"
			></div>
			<span v-if="unreadCount > 0" class="ai-unread-badge">{{ unreadCount }}</span>
		</div>
	</a>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const __ = window.__;
const hasActiveThread = ref(false);
const activeTitle = ref(__("AI Assistant"));
const isThinking = ref(false);
const unreadCount = ref(0);

const updateState = (e) => {
	try {
		hasActiveThread.value = !!(frappe.ai && frappe.ai.active_thread_browser);
		if (hasActiveThread.value && frappe.ai.active_thread_browser.subject) {
			activeTitle.value = frappe.ai.active_thread_browser.subject;
		} else {
			activeTitle.value = __("AI Assistant");
		}
	} catch (err) {
		console.error("AiHeaderButton error updating state:", err);
	}
};

const handleLoadingStart = () => {
	isThinking.value = true;
};
const handleLoadingStop = () => {
	isThinking.value = false;
};

const handleStatusUpdate = (data) => {
	if (data && data.status) {
		if (data.status === "Thinking") {
			handleLoadingStart();
		} else {
			handleLoadingStop();
		}
	}
};

const handleStreamEnd = () => {
	handleLoadingStop();
};

onMounted(() => {
	document.addEventListener("ai_thread_minimized", updateState);
	document.addEventListener("ai_thread_closed", updateState);
	document.addEventListener("ai_thread_opened", updateState);
	document.addEventListener("ai_thread_subject_changed", updateState);
	document.addEventListener("ai_thread_loading_start", handleLoadingStart);
	document.addEventListener("ai_thread_loading_stop", handleLoadingStop);

	if (frappe.realtime) {
		frappe.realtime.on("ai_thread_status_update", handleStatusUpdate);
		frappe.realtime.on("ai_thread_stream_end", handleStreamEnd);
	}

	updateState();
});

onUnmounted(() => {
	document.removeEventListener("ai_thread_minimized", updateState);
	document.removeEventListener("ai_thread_closed", updateState);
	document.removeEventListener("ai_thread_opened", updateState);
	document.removeEventListener("ai_thread_subject_changed", updateState);
	document.removeEventListener("ai_thread_loading_start", handleLoadingStart);
	document.removeEventListener("ai_thread_loading_stop", handleLoadingStop);

	if (frappe.realtime) {
		frappe.realtime.off("ai_thread_status_update", handleStatusUpdate);
		frappe.realtime.off("ai_thread_stream_end", handleStreamEnd);
	}
});

const openBrowser = () => {
	if (frappe.ai.active_thread_browser) {
		frappe.ai.active_thread_browser.dialog.show();
	} else {
		new frappe.ai.ThreadBrowser();
	}
};
</script>

<style>
/* Collapsed sidebar: hide the label (mirrors how Frappe hides .onboarding-sidebar span) */
.body-sidebar-container:not(.expanded) .ai-sidebar-label {
	display: none !important;
}

.ai-sidebar-item {
	cursor: pointer;
	margin-bottom: 10px;
	margin-top: 10px;
	padding: 4px 4px 4px 4px;
	display: flex;
	align-items: center;
	justify-content: flex-start;
}

.ai-sidebar-icon {
	margin-right: 8px;
	flex-shrink: 0;
	transition: fill 0.2s ease;
}

/* Teal accent on the icon when hovered, to hint at AI origin */
.ai-sidebar-item:hover .ai-sidebar-icon {
	fill: var(--ai-primary-color, #003f64);
}

.ai-sidebar-item.active-sidebar {
	background-color: var(--ai-primary-color, #003f64) !important;
	color: var(--white) !important;
	border-radius: 0.4rem;
}

.ai-sidebar-item.active-sidebar .ai-sidebar-icon {
	fill: var(--white) !important;
}

.ai-status-indicator {
	position: absolute;
	right: 12px;
	top: 50%;
	transform: translateY(-50%);
	display: flex;
	align-items: center;
	justify-content: center;
}
.body-sidebar-container:not(.expanded) .ai-status-indicator {
	right: 4px;
}

.ai-unread-badge {
	background-color: var(--red-500, #ef4444);
	color: white;
	font-size: 10px;
	font-weight: bold;
	line-height: 1;
	padding: 3px 6px;
	border-radius: 12px;
	min-width: 16px;
	text-align: center;
}
</style>
